import HotRadio from '@space-core/components/form/radio'
import useTranslation from '@space-core/hooks/useTranslation'
import { ThemeMode } from '@space-core/styles/themes/enum'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import clsx from 'clsx'
import styled from 'styled-components'

import type { AccessSectionProps } from './types'

const Container = styled.div<{ disabled: boolean }>`
  display: grid;
  grid-template-columns: min-content auto;

  border: ${(props) =>
    props.disabled ? '1px solid var(--gray-200)' : '1px solid var(--gray-300)'}!important;
`

const Title = styled.h3`
  font-size: 1.125rem;
`

const Radio = styled(HotRadio)<{ $isDarkMode: boolean }>`
  .hot-form__input:checked ~ .hot-form__label::before {
    background-color: ${(props) =>
      props.$isDarkMode ? 'var(--gray-100)' : 'var(--gray-900)'}!important;
  }

  .hot-form__input:hover ~ .hot-form__label::before {
    box-shadow: 0 0 0 var(--spacer-2) var(--gray-400) !important;
  }

  .hot-form__input:focus ~ .hot-form__label::before {
    box-shadow: 0 0 0 var(--input-border-width) var(--gray-400),
      0 0 0 calc(var(--spacer-1) - var(--input-border-width)) var(--white),
      0 0 0 calc(var(--spacer-1) + var(--input-border-width)) var(--gray-900) !important;
  }

  .hot-form__input:active ~ .hot-form__label::before {
    background-color: var(--gray-600) !important;
    box-shadow: 0 0 0 var(--input-border-width) var(--gray-900) !important;
  }

  .hot-form__input:checked ~ .hot-form__label::before {
    box-shadow: 0 0 0 var(--input-border-width) var(--gray-900);
  }
`

const AccessSection = ({
  title,
  description,
  checked,
  disabled,
  value,
  handleClickRadioButton,
  page
}: AccessSectionProps) => {
  const { t } = useTranslation()
  const { theme } = useThemeContext()
  const titleClass = clsx('_font-weight-light _mb-0', {
    '_text-gray-900': !disabled,
    '_text-gray-400': disabled
  })

  const descriptionClass = clsx('_text-1 _font-weight _mb-0', {
    '_text-gray-900': !disabled,
    '_text-gray-400': disabled
  })

  return (
    <Container disabled={disabled} className='_p-3 _align-items-center _w-full'>
      <Radio
        id={`${title}-${page}`}
        name={`access-and-privacy-button-${page}`}
        aria-label={`${title}-${page}`}
        data-test={`access-and-privacy-button-${value}`}
        checked={checked}
        disabled={disabled}
        className='_mr-3 _ml-2 _mb-5'
        $isDarkMode={theme === ThemeMode.DARK}
        {...(handleClickRadioButton && { onChange: () => handleClickRadioButton(value) })}
      />
      <div>
        <Title className={titleClass}>{t(title)}</Title>
        <p className={descriptionClass}>{t(description)}</p>
      </div>
    </Container>
  )
}

export default AccessSection
